import Dexie, { type EntityTable } from 'dexie'
import type {
  CachedAsset,
  CachedItem,
  LocalResponse,
  LocalSession,
  MetaRecord,
  SyncQueueEntry,
} from './types'

/**
 * The six-store schema of §6, accessed by application code only.
 *
 * Offline data lives here, in IndexedDB, enforced by omission: there is no
 * Cache Storage anywhere in this layer, and there must never be. WKWebView serves Capacitor assets from a
 * custom scheme through a scheme handler and Service Workers are generally
 * unavailable there, so a content cache built on one would work on the web and
 * fail silently on iOS. §3 calls this the most consequential design rule in the
 * specification, and this file is where it is either kept or lost.
 */
export class SciolingoDb extends Dexie {
  items!: EntityTable<CachedItem, 'id'>
  assets!: EntityTable<CachedAsset, 'id'>
  sessions!: EntityTable<LocalSession, 'id'>
  responses!: EntityTable<LocalResponse, 'id'>
  syncQueue!: EntityTable<SyncQueueEntry, 'seq'>
  meta!: EntityTable<MetaRecord, 'key'>

  constructor(name = 'sciolingo') {
    super(name)
    this.version(1).stores({
      items: 'id, version, cachedAt',
      assets: 'id, itemId',
      sessions: 'id, status, startedAtServer',
      responses: 'id, sessionId, itemId, syncState',
      syncQueue: '++seq, kind, createdAt',
      meta: 'key',
    })
  }
}

export const db = new SciolingoDb()

export async function metaGet<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key)
  return row?.value as T | undefined
}

export async function metaSet(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value, updatedAt: Date.now() })
}

export const META_KEYS = {
  auth: 'auth',
  entitlement: 'entitlement',
  clockOffsetMs: 'clockOffsetMs',
  lastSyncAt: 'lastSyncAt',
  persistGranted: 'persistGranted',
  installNudgeShown: 'installNudgeShown',
  diagnostics: 'diagnostics',
  /** What we last told the server: { transport, handle, vapidPublicKey, subscribedAt }. */
  push: 'push',
  /** The last notification this client actually received — proof the round trip closed. */
  lastPush: 'lastPush',
  /**
   * Why the last subscribe attempt failed, if it did. Persisted rather than held
   * in component state because the diagnostics card refreshes itself, and a
   * transient note is gone by the time anyone reads it.
   */
  pushError: 'pushError',
  preparation: (descriptorId: string) => `prep:${descriptorId}`,
} as const
